import type { IntegrationRequestEmailCampaign } from "@/schema/integration-request-email-campaign"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { updateIntegrationRequestEmailCampaign } from "./api"

function setCampaignInCache(queryClient: ReturnType<typeof useQueryClient>, campaign: IntegrationRequestEmailCampaign) {
  queryClient.setQueryData<IntegrationRequestEmailCampaign[]>(
    ["integration-request-email-campaigns"],
    (campaigns) =>
      campaigns?.map((candidate) => (candidate.campaign_key === campaign.campaign_key ? campaign : candidate)) ??
      campaigns
  )
}

function suppressCampaignInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  campaignKey: string,
  snoozedUntil: string | null = null
) {
  queryClient.setQueryData<IntegrationRequestEmailCampaign[]>(
    ["integration-request-email-campaigns"],
    (campaigns) =>
      campaigns?.map((campaign) =>
        campaign.campaign_key === campaignKey ? { ...campaign, shown: true, snoozed_until: snoozedUntil } : campaign
      ) ?? campaigns
  )
}

export function useMarkIntegrationRequestEmailCampaignShown() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignKey: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return updateIntegrationRequestEmailCampaign(context, campaignKey)
    },
    onMutate: (campaignKey: string) => {
      suppressCampaignInCache(queryClient, campaignKey)
    },
    onSuccess: (data: IntegrationRequestEmailCampaign) => {
      setCampaignInCache(queryClient, data)
    },
  })
}

export function useMarkIntegrationRequestEmailCampaignActed() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignKey: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return updateIntegrationRequestEmailCampaign(context, campaignKey, { acted: true })
    },
    onMutate: (campaignKey: string) => {
      suppressCampaignInCache(queryClient, campaignKey)
    },
    onSuccess: (data: IntegrationRequestEmailCampaign) => {
      setCampaignInCache(queryClient, data)
    },
  })
}

export function useSnoozeIntegrationRequestEmailCampaign() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignKey: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return updateIntegrationRequestEmailCampaign(context, campaignKey, { remindLater: true })
    },
    onMutate: (campaignKey: string) => {
      suppressCampaignInCache(queryClient, campaignKey, new Date(Date.now() + 60 * 60 * 1000).toISOString())
    },
    onSuccess: (data: IntegrationRequestEmailCampaign) => {
      setCampaignInCache(queryClient, data)
    },
  })
}
